import { useEffect, useRef, useState } from "react";
import { Check, Phone, Home, Camera, X as XIcon, ImagePlus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { trackFormStart, trackFormStep, trackFormSubmit } from "@/lib/conversionTracking";
import AuMobileInput from "@/components/AuMobileInput";
import { AU_MOBILE_ERROR, isValidAuMobile, toE164AuMobile } from "@/lib/auPhone";

interface FormData {
  name: string;
  phone: string;
  email: string;
  waste_types: string[];
  load_size: string;
  pickup_suburb: string;
  pickup_date: string;
  date_flexible: boolean;
  access: string;
  notes: string;
}

const initialForm: FormData = {
  name: "",
  phone: "",
  email: "",
  waste_types: [],
  load_size: "",
  pickup_suburb: "",
  pickup_date: "",
  date_flexible: false,
  access: "",
  notes: "",
};

const WASTE_TYPES = [
  "Household Rubbish",
  "Furniture",
  "Mattresses",
  "Appliances",
  "Garage / Shed Items",
  "Move-Out Rubbish",
  "Office / Commercial",
  "Property Clear-Out",
  "Other",
];

const LOAD_SIZES = [
  "A Few Items",
  "¼ Load",
  "½ Load",
  "¾ Load",
  "Full Load",
  "Multiple Loads",
  "Not Sure",
];

const ACCESS_OPTIONS = ["Easy Access", "Stairs", "Lift", "Difficult Access", "Not Sure"];

const TRUST = ["Upfront Quotes", "Melbourne Based", "We Do the Lifting", "Fast Response"];

const TOTAL_STEPS = 5;

const MAX_PHOTOS = 8;
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Focus ring shared by every interactive element in the form. */
const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#FDC00E]/40 focus-visible:border-[#FDC00E]";

const FloatingField = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  valid,
  type = "text",
  inputMode,
  autoComplete,
  inputRef,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  valid?: boolean;
  type?: string;
  inputMode?: "text" | "email" | "numeric";
  autoComplete?: string;
  inputRef?: React.Ref<HTMLInputElement>;
}) => {
  const errorId = `${id}-error`;
  return (
    <div>
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type={type}
          inputMode={inputMode}
          autoComplete={autoComplete}
          placeholder=" "
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={`peer w-full px-4 pt-6 pb-2 min-h-[60px] border-2 rounded-xl bg-white text-[#1a1a1a] font-medium focus:outline-none transition-all duration-200 ease-out focus:border-[#FDC00E] focus:shadow-[0_0_0_4px_rgba(253,192,14,0.18)] ${
            valid ? "pr-11" : ""
          } ${
            error
              ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_0_4px_rgba(239,68,68,0.18)]"
              : valid
                ? "border-success/60"
                : "border-[#1a1a1a]/10"
          }`}
        />
        <label
          htmlFor={id}
          className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 origin-left text-base font-medium text-[#1a1a1a]/50 transition-all duration-200 ease-out motion-reduce:transition-none peer-focus:top-[0.4rem] peer-focus:translate-y-0 peer-focus:text-xs peer-focus:font-semibold peer-focus:text-[#1a1a1a]/55 peer-[:not(:placeholder-shown)]:top-[0.4rem] peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:font-semibold peer-[:not(:placeholder-shown)]:text-[#1a1a1a]/55"
        >
          {label}
        </label>
        {valid && !error && (
          <Check
            aria-hidden="true"
            strokeWidth={3}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-success animate-in fade-in zoom-in-75 duration-200 motion-reduce:animate-none"
          />
        )}
      </div>
      {error && (
        <p id={errorId} className="text-red-500 text-xs mt-1.5 font-semibold break-words">
          {error}
        </p>
      )}
    </div>
  );
};

const SelectCard = ({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={`relative flex items-center justify-between gap-3 w-full min-h-[64px] px-5 py-4 rounded-xl border-2 text-left font-bold text-base transition-all duration-200 active:scale-[0.98] motion-reduce:active:scale-100 ${FOCUS_RING} ${
      selected
        ? "border-[#F64A01] bg-orange-soft text-[#1a1a1a] shadow-[0_8px_24px_rgba(246,74,1,0.18)]"
        : "border-[#1a1a1a]/10 bg-white text-[#1a1a1a] hover:border-[#F64A01] hover:bg-orange-soft hover:shadow-[0_6px_18px_rgba(246,74,1,0.12)]"
    }`}
  >
    <span>{label}</span>
    <span
      className={`flex items-center justify-center w-6 h-6 rounded-full shrink-0 transition-all duration-200 motion-reduce:transition-none ${
        selected ? "bg-[#F64A01] scale-100 opacity-100" : "scale-50 opacity-0"
      }`}
    >
      <Check className="w-4 h-4 text-white" strokeWidth={3} />
    </span>
  </button>
);

interface PhotoItem {
  file: File;
  url: string;
}

const QuoteForm = () => {
  const [step, setStep] = useState(1);
  const [dir, setDir] = useState<"fwd" | "back">("fwd");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormData>(initialForm);
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof FormData, boolean>>>({});
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!submitted) firstFieldRef.current?.focus({ preventScroll: true });
  }, [step, submitted]);

  // Release object URLs on unmount only (photosRef keeps the latest list)
  const photosRef = useRef<PhotoItem[]>([]);
  photosRef.current = photos;
  useEffect(
    () => () => {
      photosRef.current.forEach((p) => URL.revokeObjectURL(p.url));
    },
    []
  );


  const setField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackFormStart("quote_wizard");
    }
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const toggleWasteType = (option: string) =>
    setField(
      "waste_types",
      form.waste_types.includes(option)
        ? form.waste_types.filter((t) => t !== option)
        : [...form.waste_types, option]
    );

  const markTouched = (field: keyof FormData) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const addFiles = (list: FileList | null) => {
    if (!list?.length) return;
    const incoming = Array.from(list).filter((f) => f.type.startsWith("image/"));
    const tooBig = incoming.filter((f) => f.size > MAX_PHOTO_BYTES);
    if (tooBig.length) toast.error("Photos must be under 10MB each.");
    const accepted = incoming.filter((f) => f.size <= MAX_PHOTO_BYTES);
    setPhotos((prev) => {
      const room = MAX_PHOTOS - prev.length;
      if (room <= 0) {
        toast.error(`You can upload up to ${MAX_PHOTOS} photos.`);
        return prev;
      }
      return [
        ...prev,
        ...accepted.slice(0, room).map((file) => ({ file, url: URL.createObjectURL(file) })),
      ];
    });
  };

  const removePhoto = (index: number) =>
    setPhotos((prev) => {
      URL.revokeObjectURL(prev[index].url);
      return prev.filter((_, i) => i !== index);
    });

  /** Pure per step validity, used for the progress ticks. */
  const isStepComplete = (n: number): boolean => {
    if (n === 1)
      return (
        Boolean(form.name.trim()) &&
        isValidAuMobile(form.phone) &&
        EMAIL_RE.test(form.email.trim())
      );
    if (n === 2) return form.waste_types.length > 0;
    if (n === 3) return Boolean(form.load_size);
    if (n === 4) return photos.length > 0;
    if (n === 5) return Boolean(form.pickup_suburb.trim()) && Boolean(form.access);
    return false;
  };

  const fieldValid = (field: keyof FormData): boolean => {
    if (!touched[field] || errors[field]) return false;
    if (field === "phone") return isValidAuMobile(form.phone);
    if (field === "email") return EMAIL_RE.test(form.email.trim());
    return Boolean(String(form[field]).trim());
  };

  const validateStep = (current: number): boolean => {
    const e: Partial<Record<keyof FormData, string>> = {};
    if (current === 1) {
      if (!form.name.trim()) e.name = "Name is required";
      if (!form.phone.trim()) e.phone = "Phone number is required";
      else if (!isValidAuMobile(form.phone)) e.phone = AU_MOBILE_ERROR;
      if (!form.email.trim()) e.email = "Email is required";
      else if (!EMAIL_RE.test(form.email.trim()))
        e.email = "Enter a valid email address";
    }
    if (current === 2 && form.waste_types.length === 0)
      e.waste_types = "Please choose at least one option";
    if (current === 3 && !form.load_size) e.load_size = "Please choose a rough load size";
    if (current === 5) {
      if (!form.pickup_suburb.trim()) e.pickup_suburb = "Pickup address or suburb is required";
      if (!form.access) e.access = "Please choose the access type";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validateStep(step)) {
      firstFieldRef.current?.focus({ preventScroll: true });
      return;
    }
    trackFormStep("quote_wizard", step, TOTAL_STEPS);
    setDir("fwd");
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };

  const back = () => {
    setErrors({});
    setDir("back");
    setStep((s) => Math.max(1, s - 1));
  };

  /** Uploads the selected photos to the private quote-photos bucket. */
  const uploadPhotos = async (quoteRequestId: string): Promise<string[]> => {
    const paths: string[] = [];
    for (const [i, photo] of photos.entries()) {
      const ext = photo.file.name.split(".").pop()?.toLowerCase() || "jpg";
      const path = `${quoteRequestId}/${i + 1}-${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage
        .from("quote-photos")
        .upload(path, photo.file, { contentType: photo.file.type, upsert: false });
      if (!error) paths.push(path);
    }
    return paths;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step < TOTAL_STEPS) {
      next();
      return;
    }
    if (!validateStep(TOTAL_STEPS)) return;

    setLoading(true);
    const submittedAt = new Date();
    const quoteRequestId = crypto.randomUUID();
    const phoneE164 = toE164AuMobile(form.phone);

    const photoPaths = photos.length ? await uploadPhotos(quoteRequestId) : [];

    const details = [
      `Rubbish type: ${form.waste_types.join(", ")}`,
      `Load size: ${form.load_size}`,
      `Access: ${form.access}`,
      form.date_flexible ? "Pickup date: Flexible" : null,
      photoPaths.length ? `Photos (${photoPaths.length}): ${photoPaths.join(", ")}` : "Photos: none",
      form.notes.trim() ? `Notes: ${form.notes.trim()}` : null,
    ]
      .filter(Boolean)
      .join(" • ");

    const { error } = await supabase.from("quote_requests").insert({
      id: quoteRequestId,
      name: form.name.trim(),
      phone: phoneE164,
      email: form.email.trim(),
      pickup_suburb: form.pickup_suburb.trim(),
      dropoff_suburb: "Disposal / rubbish removal",
      move_date: form.date_flexible ? null : form.pickup_date || null,
      details,
    });

    if (error) {
      setLoading(false);
      toast.error("Something went wrong. Please try again or call us directly.");
      return;
    }

    try {
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "new-quote-request",
          quoteRequestId,
          idempotencyKey: `new-quote-${phoneE164}-${submittedAt.getTime()}`,
        },
      });
    } catch {
      // Never block the customer confirmation on the notification email
    }

    try {
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "quote-confirmation",
          quoteRequestId,
          idempotencyKey: `quote-confirmation-${form.email.trim()}-${submittedAt.getTime()}`,
        },
      });
    } catch {
      // Never block the success state on the confirmation email
    }

    setLoading(false);
    trackFormStep("quote_wizard", TOTAL_STEPS, TOTAL_STEPS);
    void trackFormSubmit(
      "quote_wizard",
      {
        waste_types: form.waste_types.join(", "),
        load_size: form.load_size,
        access: form.access,
        photos: photoPaths.length,
        pickup_suburb: form.pickup_suburb.trim(),
      },
      { email: form.email, phone: phoneE164 },
    );
    setSubmitted(true);
  };

  const inputClass = (field: keyof FormData) =>
    `w-full px-4 py-4 min-h-[60px] border-2 rounded-xl bg-white text-[#1a1a1a] font-medium placeholder:text-[#1a1a1a]/40 focus:border-[#FDC00E] focus:outline-none focus:shadow-[0_0_0_4px_rgba(253,192,14,0.18)] transition-all duration-200 ease-out ${
      errors[field]
        ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_0_4px_rgba(239,68,68,0.18)]"
        : fieldValid(field)
          ? "border-[#F64A01]/60"
          : "border-[#1a1a1a]/10"
    }`;

  const primaryBtn =
    `w-full bg-[#1a1a1a] text-white py-4 text-lg font-bold rounded-xl transition-all duration-200 ease-out shadow-[0_6px_16px_rgba(0,0,0,0.12)] hover:opacity-95 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(0,0,0,0.18)] active:translate-y-0 active:scale-[0.99] active:shadow-[0_4px_12px_rgba(0,0,0,0.14)] motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:active:scale-100 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-[0_6px_16px_rgba(0,0,0,0.12)] ${FOCUS_RING}`;

  const errorText = (field: keyof FormData) =>
    errors[field] ? (
      <p id={`${field}-error`} className="text-red-500 text-xs mt-1.5 font-semibold break-words">
        {errors[field]}
      </p>
    ) : null;

  return (
    <section id="quote" className="scroll-mt-28 bg-[#f7f7f7] py-16 md:py-20">
      <div className="container max-w-2xl">
        {submitted ? (
          <div className="bg-white border-2 border-success rounded-2xl p-8 md:p-10 text-center shadow-[0_20px_50px_rgba(0,0,0,0.06)]">
            <div className="mx-auto mb-5 w-14 h-14 rounded-full bg-success-soft flex items-center justify-center">
              <Check className="w-8 h-8 text-success" strokeWidth={3} />
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] mb-3">Thanks!</h2>
            <p className="text-[#1a1a1a]/75 mb-1">We've received your quote request.</p>
            <p className="text-[#1a1a1a]/75 mb-8">
              One of our team will contact you shortly with pricing and pickup availability.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="tel:1800556654"
                data-call-source="quote_success"
                aria-label="Call 1800 556 654"
                className="inline-flex items-center justify-center gap-2 bg-[#1a1a1a] text-white px-6 py-4 rounded-xl font-bold hover:bg-orange transition-colors"
              >
                <Phone className="w-5 h-5" /> Call 1800 556 654
              </a>
              <a
                href="/"
                className="inline-flex items-center justify-center gap-2 bg-white border-2 border-[#1a1a1a]/15 text-[#1a1a1a] px-6 py-4 rounded-xl font-bold hover:border-[#1a1a1a]/40 transition-colors"
              >
                <Home className="w-5 h-5" /> Back to Home
              </a>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-[#1a1a1a]/8 shadow-[0_20px_50px_rgba(0,0,0,0.06)] p-6 md:p-10">
            {/* Progress */}
            <div
              className="flex justify-center mb-8"
              role="group"
              aria-label={`Step ${step} of ${TOTAL_STEPS}`}
            >
              <div className="flex items-center gap-2 sm:gap-2.5" aria-hidden="true">
                {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
                  const n = i + 1;
                  const completed = n !== step && isStepComplete(n);
                  const current = n === step;
                  return (
                    <span
                      key={i}
                      className={`flex items-center justify-center rounded-full transition-all duration-300 ease-out motion-reduce:transition-none ${
                        current
                          ? "w-7 h-2.5 bg-[#F64A01]"
                          : completed
                            ? "w-5 h-5 bg-success-soft ring-1 ring-success/50"
                            : "w-2.5 h-2.5 bg-[#1a1a1a]/15"
                      }`}
                    >
                      {completed && (
                        <Check
                          strokeWidth={3.5}
                          className="w-3 h-3 text-success animate-in fade-in zoom-in-75 duration-300 motion-reduce:animate-none"
                        />
                      )}
                    </span>
                  );
                })}
              </div>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div
                key={step}
                className={`animate-in fade-in duration-300 motion-reduce:slide-in-from-right-0 motion-reduce:slide-in-from-left-0 ${
                  dir === "fwd" ? "slide-in-from-right-4" : "slide-in-from-left-4"
                }`}
              >
                {step === 1 && (
                  <>
                    <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] mb-2">
                      Let's Get It Gone.
                    </h2>
                    <p className="text-[#1a1a1a]/75 mb-7">
                      Tell us what needs removing and we'll get your quote started.
                    </p>
                    <div className="space-y-4">
                      <FloatingField
                        id="quote-name"
                        label="Full Name"
                        inputRef={firstFieldRef}
                        autoComplete="name"
                        inputMode="text"
                        value={form.name}
                        onChange={(v) => setField("name", v)}
                        onBlur={() => markTouched("name")}
                        error={errors.name}
                        valid={fieldValid("name")}
                      />
                      <div>
                        <AuMobileInput
                          id="quote-phone"
                          label="Phone Number"
                          errorId="phone-error"
                          isValid={fieldValid("phone")}
                          value={form.phone}
                          onChange={(v) => setField("phone", v)}
                          onBlur={() => {
                            markTouched("phone");
                            if (form.phone.trim() && !isValidAuMobile(form.phone))
                              setErrors((prev) => ({ ...prev, phone: AU_MOBILE_ERROR }));
                          }}
                          hasError={Boolean(errors.phone)}
                          inputClassName="w-full px-4 bg-white text-[#1a1a1a] font-medium placeholder:text-[#1a1a1a]/40 focus:outline-none"
                          ariaLabel="Australian mobile number"
                        />
                        {errorText("phone")}
                      </div>
                      <FloatingField
                        id="quote-email"
                        label="Email Address"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        value={form.email}
                        onChange={(v) => setField("email", v)}
                        onBlur={() => markTouched("email")}
                        error={errors.email}
                        valid={fieldValid("email")}
                      />
                    </div>
                  </>
                )}

                {step === 2 && (
                  <>
                    <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] mb-2">
                      What Needs Removing?
                    </h2>
                    <p className="text-[#1a1a1a]/75 mb-7">Choose everything that applies.</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {WASTE_TYPES.map((option) => (
                        <SelectCard
                          key={option}
                          label={option}
                          selected={form.waste_types.includes(option)}
                          onClick={() => toggleWasteType(option)}
                        />
                      ))}
                    </div>
                    {errorText("waste_types")}
                  </>
                )}

                {step === 3 && (
                  <>
                    <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] mb-7">
                      How Much Is There?
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {LOAD_SIZES.map((option) => (
                        <SelectCard
                          key={option}
                          label={option}
                          selected={form.load_size === option}
                          onClick={() => setField("load_size", option)}
                        />
                      ))}
                    </div>
                    {errorText("load_size")}
                  </>
                )}

                {step === 4 && (
                  <>
                    <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] mb-2">
                      Upload Photos
                    </h2>
                    <p className="text-[#1a1a1a]/75 mb-7">
                      Photos help us assess the amount and provide a more accurate quote.
                    </p>

                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                      }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        addFiles(e.dataTransfer.files);
                      }}
                      className={`rounded-2xl border-2 border-dashed p-8 md:p-10 text-center transition-all duration-200 ${
                        dragOver
                          ? "border-[#F64A01] bg-[#FFF1E8]"
                          : "border-[#1a1a1a]/15 bg-[#f7f7f7] hover:border-[#F64A01]/50"
                      }`}
                    >
                      <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-[#FDC00E]/25 flex items-center justify-center">
                        <Camera className="w-7 h-7 text-[#1a1a1a]" />
                      </div>
                      <p className="font-bold text-[#1a1a1a] mb-1">
                        Drag photos here or tap to upload
                      </p>
                      <p className="text-sm text-[#1a1a1a]/60 mb-5">
                        Up to {MAX_PHOTOS} photos, 10MB each. JPG or PNG.
                      </p>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className={`inline-flex items-center gap-2 bg-[#1a1a1a] text-white px-6 py-3 rounded-xl font-bold hover:bg-orange transition-colors ${FOCUS_RING}`}
                      >
                        <ImagePlus className="w-5 h-5" />
                        Choose Photos
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="sr-only"
                        aria-label="Upload photos of the rubbish"
                        onChange={(e) => {
                          addFiles(e.target.files);
                          e.target.value = "";
                        }}
                      />
                    </div>

                    {photos.length > 0 && (
                      <ul className="mt-5 grid grid-cols-3 sm:grid-cols-4 gap-3">
                        {photos.map((p, i) => (
                          <li key={p.url} className="relative aspect-square">
                            <img
                              src={p.url}
                              alt={`Uploaded photo ${i + 1}`}
                              className="w-full h-full object-cover rounded-xl border border-[#1a1a1a]/10"
                            />
                            <button
                              type="button"
                              onClick={() => removePhoto(i)}
                              aria-label={`Remove photo ${i + 1}`}
                              className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#1a1a1a] text-white flex items-center justify-center hover:bg-orange transition-colors"
                            >
                              <XIcon className="w-4 h-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    <p className="mt-5 text-sm text-[#1a1a1a]/55 text-center">
                      No photos handy? You can skip this step.
                    </p>
                  </>
                )}

                {step === 5 && (
                  <>
                    <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] mb-7">
                      Pickup Details
                    </h2>
                    <div className="space-y-4">
                      <FloatingField
                        id="quote-pickup"
                        label="Pickup Address or Suburb"
                        inputRef={firstFieldRef}
                        autoComplete="address-level2"
                        value={form.pickup_suburb}
                        onChange={(v) => setField("pickup_suburb", v)}
                        onBlur={() => markTouched("pickup_suburb")}
                        error={errors.pickup_suburb}
                        valid={fieldValid("pickup_suburb")}
                      />
                      <div>
                        <label
                          htmlFor="quote-pickup-date"
                          className="block text-sm font-bold text-[#1a1a1a] mb-2"
                        >
                          Preferred Pickup Date
                        </label>
                        <input
                          type="date"
                          id="quote-pickup-date"
                          aria-label="Preferred pickup date"
                          disabled={form.date_flexible}
                          value={form.pickup_date}
                          onChange={(e) => {
                            markTouched("pickup_date");
                            setField("pickup_date", e.target.value);
                          }}
                          className={`${inputClass("pickup_date")} disabled:opacity-50 disabled:cursor-not-allowed`}
                        />
                        <div className="mt-3">
                          <SelectCard
                            label="My pickup date is flexible"
                            selected={form.date_flexible}
                            onClick={() => {
                              const flexible = !form.date_flexible;
                              setField("date_flexible", flexible);
                              if (flexible) setField("pickup_date", "");
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <p className="text-sm font-bold text-[#1a1a1a] mt-7 mb-3">
                      Stairs or difficult access?
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {ACCESS_OPTIONS.map((option) => (
                        <SelectCard
                          key={option}
                          label={option}
                          selected={form.access === option}
                          onClick={() => setField("access", option)}
                        />
                      ))}
                    </div>
                    {errorText("access")}

                    <label
                      htmlFor="quote-notes"
                      className="block text-sm font-bold text-[#1a1a1a] mt-7 mb-2"
                    >
                      Additional Notes
                    </label>
                    <textarea
                      id="quote-notes"
                      rows={4}
                      value={form.notes}
                      onChange={(e) => setField("notes", e.target.value)}
                      placeholder="Anything else we should know?"
                      className="w-full px-4 py-3 border-2 border-[#1a1a1a]/10 rounded-xl bg-white text-[#1a1a1a] font-medium placeholder:text-[#1a1a1a]/40 focus:border-[#FDC00E] focus:outline-none focus:shadow-[0_0_0_4px_rgba(253,192,14,0.18)] transition-all duration-200"
                    />
                  </>
                )}
              </div>

              <div className="mt-8 space-y-3">
                <button type="submit" disabled={loading} className={primaryBtn}>
                  {step === 1 && "Next → What's Going"}
                  {step === 2 && "Next → How Much"}
                  {step === 3 && "Next → Photos"}
                  {step === 4 && "Next → Pickup Details"}
                  {step === 5 && (loading ? "Sending…" : "Get My Quote")}
                </button>
                {step > 1 && (
                  <button
                    type="button"
                    onClick={back}
                    className={`w-full py-3 rounded-xl text-sm font-bold text-[#1a1a1a]/70 hover:text-[#1a1a1a] transition-colors ${FOCUS_RING}`}
                  >
                    ← Back
                  </button>
                )}
              </div>
            </form>

            <div className="mt-6 pt-6 border-t border-[#1a1a1a]/8 flex flex-wrap justify-center gap-x-5 gap-y-2">
              {TRUST.map((item) => (
                <span
                  key={item}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1a1a1a]/70"
                >
                  <Check className="w-3.5 h-3.5 text-success" strokeWidth={3} />
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default QuoteForm;
